import { TextField } from '@material-ui/core';
import MyModal from 'components/MyModal';
import Spin from 'components/Spin';
import React, { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import accessControlService from 'v2/services/access-control';

import type { Group } from 'v2/interfaces/access-control';

import FormGroupModule from './Module';

type Props = {
  current?: Group;
  onComplete: () => void;
  onCancel: () => void;
};

export default function FormGroup({ onCancel, onComplete, current }: Props) {
  const [isLoadingModules] = useObservable(() => accessControlService.isLoadingModules$, []);
  const [modules] = useObservable(() => accessControlService.getModules(), []);
  const [saveGroup] = useCallbackObservable((data) => accessControlService.saveGroup(data), []);
  const [updateGroup] = useCallbackObservable((data) => accessControlService.updateGroup(data), []);
  const [groupName, setGroupName] = useState(current?.name || '');
  const [selectedActionsId, setSelectedActionsId] = useState(() => {
    if (!current) return [];
    const actionsData = Object.values(current.privileges).flatMap((privilege) => privilege.actions);
    return actionsData.map((action) => action.id);
  });
  const [isSaving, setIsSaving] = useState(false);

  const groupNameInputRef = useRef<HTMLInputElement>(null);

  const isEditing = !!current;

  function handleModalSubmit() {
    setIsSaving(true);
    if (!groupName) {
      groupNameInputRef.current.focus();
      toast.error('O nome do grupo é obrigatório.');
      setIsSaving(false);
      return;
    }
    if (!selectedActionsId.length) {
      toast.error('O grupo precisa ter pelo menos uma ação.');
      setIsSaving(false);
      return;
    }
    if (isEditing) {
      toast.success('Sucesso, grupo atualizado!');
      updateGroup({
        id: current.id,
        name: groupName,
        actions: selectedActionsId,
      });
    } else {
      toast.success('Sucesso, grupo criado!');
      saveGroup({
        name: groupName,
        actions: selectedActionsId,
      });
    }
    onComplete();
    setIsSaving(false);
  }

  const handleActionsChange = useCallback((id: number) => {
    setSelectedActionsId((old) => {
      if (old.includes(id)) {
        return old.filter((actionId) => actionId !== id);
      }
      return [...old, id];
    });
  }, []);

  return (
    <MyModal
      hasActive
      title='Grupo de ações'
      subTitle='Adicione os módulos e as ações de cada módulo para criar um grupo.'
      closeModal={onCancel}
      textButtonOk={isEditing ? 'Atualizar' : 'Adicionar'}
      hasButtons={true}
      handleClickOk={handleModalSubmit}
      loading={isSaving || isLoadingModules}
    >
      {isLoadingModules && <Spin />}

      {!isLoadingModules && (
        <>
          <TextField
            label='Nome do grupo*'
            inputRef={groupNameInputRef}
            fullWidth
            value={groupName}
            disabled={isSaving}
            onChange={(e) => setGroupName(e.target.value)}
            style={{
              marginBottom: 16,
            }}
          />

          {!!modules?.length &&
            modules.map((module, index) => (
              <FormGroupModule
                key={module.id}
                handleActionsChange={handleActionsChange}
                selectedActionsId={selectedActionsId}
                module={module}
                initialOpen={index === 0}
              />
            ))}
        </>
      )}
    </MyModal>
  );
}
